import { z } from 'zod'

export const workflowPatternSchema = z.enum(['chaining', 'parallel', 'routing'])
export type WorkflowPattern = z.infer<typeof workflowPatternSchema>

export const workflowStatusSchema = z.enum(['draft', 'active', 'inactive'])
export type WorkflowStatus = z.infer<typeof workflowStatusSchema>

export const schemaFieldSchema = z.object({
  name: z.string().min(1).max(100),
  type: z.enum(['string', 'number', 'boolean', 'array', 'object']),
  required: z.boolean().default(false),
  description: z.string().max(500).optional(),
})
export type SchemaField = z.infer<typeof schemaFieldSchema>

export const nodeVariableMappingSchema = z.object({
  source_node_id: z.string().min(1),
  source_output: z.string().min(1),
  target_node_id: z.string().min(1),
  target_input: z.string().min(1),
})
export type NodeVariableMapping = z.infer<typeof nodeVariableMappingSchema>

export const workflowCreateSchema = z.object({
  name: z.string().min(2).max(255),
  description: z.string().max(2000).optional(),
  pattern_type: workflowPatternSchema,
})
export type WorkflowCreateInput = z.infer<typeof workflowCreateSchema>

export const nodeSchema = z.object({
  id: z.string(),
  type: z.string(),
  label: z.string(),
  position: z.number(),
  inputs: z.array(z.object({ name: z.string(), type: z.string() })),
  outputs: z.array(z.object({ name: z.string(), type: z.string() })),
})
export type WorkflowNode = z.infer<typeof nodeSchema>

// Alias used by lifecycle schemas / export contract (03-04).
export const workflowNodeSchema = nodeSchema

export const edgeSchema = z.object({
  source: z.string(),
  target: z.string(),
  condition: z.string().optional(),
})
export type WorkflowEdge = z.infer<typeof edgeSchema>

// Alias used by lifecycle schemas / export contract (03-04).
export const workflowEdgeSchema = edgeSchema

// --- Phase 03 lifecycle types (versioning, diff, import/export) ---

export interface WorkflowVersion {
  id: string
  workflow_id: string
  version_number: number
  nodes: WorkflowNode[]
  edges: WorkflowEdge[]
  settings: Record<string, unknown>
  input_schema: Record<string, unknown>
  output_schema: Record<string, unknown>
  pattern_type: string
  created_by_id: string | null
  note: string | null
  created_at: string
}

export interface WorkflowVersionListResponse {
  versions: WorkflowVersion[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export type DiffState = 'added' | 'removed' | 'modified' | 'unchanged'

export interface NodeDiff {
  id: string
  state: DiffState
  old: Record<string, unknown> | null
  new: Record<string, unknown> | null
  changed_fields: string[]
}

export interface EdgeDiff {
  id: string
  state: DiffState
  old: Record<string, unknown> | null
  new: Record<string, unknown> | null
  changed_fields: string[]
}

export interface WorkflowDiff {
  from_version: number
  to_version: number
  nodes: NodeDiff[]
  edges: EdgeDiff[]
  summary: {
    added_nodes: number
    removed_nodes: number
    modified_nodes: number
    added_edges: number
    removed_edges: number
    modified_edges: number
  }
}

export interface WorkflowImportResponse {
  workflow: Workflow
  needs_attention_nodes: string[]
}

export interface PublishRequest {
  note?: string | null
}

export interface Workflow {
  id: string
  project_id: string
  org_id: string | null
  name: string
  description: string | null
  pattern_type: WorkflowPattern
  input_schema: Record<string, unknown>
  output_schema: Record<string, unknown>
  nodes: WorkflowNode[]
  edges: WorkflowEdge[]
  settings: Record<string, unknown>
  version: number
  status: WorkflowStatus
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface WorkflowListResponse {
  workflows: Workflow[]
  total: number
  page: number
  page_size: number
  total_pages: number
}
